import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, ArrowUUpLeft, WarningOctagon, Clock } from "@phosphor-icons/react";

/**
 * v3.2.35 · Admin drill-down: full payment history for one user across
 * both roles they might play (client + PE). Reached from AdminUsersPage
 * via a "transactions" button per row.
 */
export default function AdminUserTransactionsPage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  // v3.2.37 · Admin commission override state.
  const [commission, setCommission] = useState("");
  const [commissionBusy, setCommissionBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/admin/users/${userId}/transactions`);
        setData(res.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
      } finally { setBusy(false); }
    })();
  }, [userId]);

  const saveCommission = async () => {
    const v = Number(commission);
    if (isNaN(v) || v < 0 || v > 50) {
      toast.error("Commission must be between 0 and 50 %.");
      return;
    }
    setCommissionBusy(true);
    try {
      await api.post(`/admin/users/${userId}/commission`, { commission_pct: v });
      toast.success(`Commission set to ${v}% for this PE.`);
      setCommission("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setCommissionBusy(false); }
  };

  const chip = (r) => {
    if (r.dispute_status === "open") return { cls: "border-[#FF3B30]/50 text-[#FF3B30] bg-[#FF3B30]/10", icon: <WarningOctagon size={11} weight="fill" />, label: "disputed" };
    if (r.payment_status === "refunded") return { cls: "border-gray-500/40 text-gray-400 bg-black/30", icon: <ArrowUUpLeft size={11} />, label: "refunded" };
    if (r.payment_status === "paid") return { cls: "border-emerald-400/50 text-emerald-400 bg-emerald-400/10", icon: <CheckCircle size={11} weight="fill" />, label: "paid" };
    return { cls: "border-[#FFCC00]/40 text-[#FFCC00] bg-[#FFCC00]/10", icon: <Clock size={11} />, label: r.payment_status || "pending" };
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="admin-users" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <button onClick={() => nav("/admin/users")}
          className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1"
          data-testid="admin-tx-back">
          <ArrowLeft size={14} /> users
        </button>

        <div className="mt-4 label-tag">// Admin · transactions</div>
        {busy ? (
          <div className="mt-3 text-gray-500 font-mono text-sm">Loading…</div>
        ) : !data ? null : (
          <>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter"
                data-testid="admin-tx-heading">
              {data.user.name || data.user.email}
            </h1>
            <div className="mt-1 text-xs font-mono text-gray-400">
              {data.user.email} · role · <span className="text-[#00E5FF]">{data.user.role}</span>
            </div>

            {/* v3.2.37 · Admin commission override — only for PE targets. */}
            {data.user.role === "pe" && (
              <div className="mt-6 border border-white/10 bg-white/[0.02] p-4"
                   data-testid="admin-tx-commission-card">
                <div className="label-tag">// commission override</div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <div className="text-xs font-mono text-gray-400">
                    Set a per-PE platform fee (default <span className="text-white">10%</span>).
                    Applies to future payments only.
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" max="50" step="0.5"
                      value={commission}
                      onChange={(e) => setCommission(e.target.value)}
                      placeholder="e.g. 7.5"
                      className="w-24 bg-black/50 border border-white/10 focus:border-[#00E5FF] px-2 py-1 text-sm text-white outline-none font-mono"
                      data-testid="admin-tx-commission-input"
                    />
                    <span className="text-sm text-gray-400 font-mono">%</span>
                    <button onClick={saveCommission}
                      disabled={commissionBusy || commission === ""}
                      className="btn-primary text-xs py-2 px-3 disabled:opacity-40"
                      data-testid="admin-tx-commission-save">
                      {commissionBusy ? "saving…" : "save"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Client bucket — money out */}
            <Bucket title="As client · money spent"
              summary={[
                { label: "Total paid", value: `$${Number(data.as_client.total_paid).toLocaleString()}`, accent: "#00E5FF" },
                { label: "Refunded",   value: `$${Number(data.as_client.total_refunded).toLocaleString()}` },
                { label: "Transactions", value: data.as_client.count },
              ]}
              rows={data.as_client.rows}
              emptyLabel="No payments made."
              amountKey="amount_usd"
              feeKey="platform_fee_usd"
              feeLabel="platform"
              chip={chip}
              testPrefix="admin-tx-client"
            />

            {/* PE bucket — money in */}
            <Bucket title="As PE · money earned"
              summary={[
                { label: "Total earned", value: `$${Number(data.as_pe.total_earnings).toLocaleString()}`, accent: "#00E5FF" },
                { label: "Refunded", value: `$${Number(data.as_pe.total_refunded).toLocaleString()}` },
                { label: "Transactions", value: data.as_pe.count },
              ]}
              rows={data.as_pe.rows}
              emptyLabel="No earnings received."
              amountKey="amount_usd"
              feeKey="pe_earnings_usd"
              feeLabel="earned"
              chip={chip}
              testPrefix="admin-tx-pe"
            />
          </>
        )}
      </div>
    </div>
  );
}

function Bucket({ title, summary, rows, emptyLabel, amountKey, feeKey, feeLabel, chip, testPrefix }) {
  if (!rows) return null;
  return (
    <div className="mt-8" data-testid={testPrefix}>
      <div className="label-tag mb-3">// {title}</div>
      <div className="grid grid-cols-3 gap-3">
        {summary.map((s, i) => (
          <div key={i} className="border border-white/10 bg-white/[0.02] p-3"
               data-testid={`${testPrefix}-stat-${i}`}>
            <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500">{s.label}</div>
            <div className="mt-1 font-display font-black text-xl tabular-nums"
                 style={{ color: s.accent || "#FFFFFF" }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="mt-3 border border-white/10 bg-white/5 p-4 text-xs font-mono text-gray-500 text-center"
             data-testid={`${testPrefix}-empty`}>
          {emptyLabel}
        </div>
      ) : (
        <div className="mt-3 border border-white/10" data-testid={`${testPrefix}-table`}>
          <div className="hidden md:grid grid-cols-[2fr_120px_120px_120px_100px] gap-2 px-4 py-2 border-b border-white/10 bg-black/40 font-mono text-[10px] uppercase tracking-wider text-gray-500">
            <div>Job · Milestone</div>
            <div>Amount</div>
            <div>{feeLabel}</div>
            <div>Date</div>
            <div>Status</div>
          </div>
          {rows.map((r) => {
            const c = chip(r);
            const when = r.paid_at || r.created_at;
            return (
              <div key={r.session_id}
                className="grid grid-cols-1 md:grid-cols-[2fr_120px_120px_120px_100px] gap-2 px-4 py-3 border-b border-white/5 items-center hover:bg-white/[0.02]"
                data-testid={`${testPrefix}-row-${r.session_id}`}>
                <div className="min-w-0">
                  <Link to={`/jobs/${r.job_id}`}
                    className="text-white text-sm truncate hover:text-[#00E5FF] block">
                    MS{r.milestone_idx} · {r.milestone_title || <span className="italic text-gray-500">(untitled)</span>}
                  </Link>
                  <div className="text-[10px] font-mono text-gray-500 truncate">{r.job_title}</div>
                </div>
                <div className="font-display font-black tabular-nums text-white">
                  ${Number(r[amountKey] || 0).toLocaleString()}
                </div>
                <div className="text-sm text-gray-300 tabular-nums">
                  ${Number(r[feeKey] || 0).toLocaleString()}
                </div>
                <div className="text-xs font-mono text-gray-400">
                  {when ? new Date(when).toLocaleDateString() : "—"}
                </div>
                <div>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${c.cls}`}
                        data-testid={`${testPrefix}-status-${r.session_id}`}>
                    {c.icon} {c.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
